import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { parseFunctions, type Workspace, type WorkspaceProfileProposal } from 'shared'
import { Alert, Badge, Button, Card } from '../ui'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { formatDate } from '../../lib/dates'

interface Props {
  workspaceId: string
  /** Current profile, to show each proposal as a difference against what is live. */
  current: Workspace
  /** Called after an accepted proposal changed the profile, so the form reloads. */
  onAccepted: () => void
}

const display = (value: unknown) => (value === null || value === undefined || value === '' ? '—' : String(value))

/** The text for a value: coded fields (kind, revenue band, functions) read as the form shows them. */
function useDisplay() {
  const { t } = useTranslation()
  return (field: string, value: unknown): string => {
    if (value === null || value === undefined || value === '') return display(value)
    const text = String(value)
    if (field === 'kind') return t(`workspaces.kinds.${text}`, text)
    if (field === 'revenue_range') return t(`workspaces.revenueRanges.${text}`, text)
    if (field === 'functions_involved') {
      return parseFunctions(text)
        .map((code) => t(`workspaces.functions.${code}`, code))
        .join(', ')
    }
    return text
  }
}

/**
 * What the agent proposed for the company profile. Nothing here has changed
 * the profile yet: accepting is the one action that does.
 */
export default function ProfileProposalsPanel({ workspaceId, current, onAccepted }: Props) {
  const { t, i18n } = useTranslation()
  const live: Record<string, unknown> = { ...current }

  const [proposals, setProposals] = useState<WorkspaceProfileProposal[]>([])
  const [busy, setBusy] = useState<{ id: string; action: 'accept' | 'reject' } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const response = await api.get<{ data: WorkspaceProfileProposal[] }>(
      `/api/workspaces/${workspaceId}/profile-proposals`,
    )
    setProposals(response.data)
  }, [workspaceId])

  useEffect(() => {
    load().catch((err) => setError(toErrorMessage(err)))
  }, [load])

  const decide = async (proposal: WorkspaceProfileProposal, action: 'accept' | 'reject') => {
    setBusy({ id: proposal.id, action })
    setError(null)
    try {
      await api.post(`/api/workspaces/${workspaceId}/profile-proposals/${proposal.id}/${action}`, {})
      if (action === 'accept') onAccepted()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setBusy(null)
      // Other proposals may have arrived or been decided meanwhile.
      await load().catch(() => undefined)
    }
  }

  const pending = proposals.filter((proposal) => proposal.status === 'pending')
  const decided = proposals.filter((proposal) => proposal.status !== 'pending')

  if (proposals.length === 0 && !error) return null

  return (
    <div className="mb-6 space-y-4">
      {error && <Alert>{error}</Alert>}

      {pending.length > 0 && (
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
          {t('profileProposals.title')}
        </h3>
      )}

      {pending.map((proposal) => (
        <Card key={proposal.id} className="p-6">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('profileProposals.proposedBy', {
              name: proposal.proposed_by_name ?? '—',
              when: formatDate(proposal.created_at, i18n.language),
            })}
          </p>
          {proposal.rationale && (
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{proposal.rationale}</p>
          )}

          <ChangesList changes={proposal.changes} before={live} />

          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="secondary"
              loading={busy?.id === proposal.id && busy.action === 'reject'}
              disabled={busy?.id === proposal.id}
              onClick={() => decide(proposal, 'reject')}
            >
              {t('profileProposals.reject')}
            </Button>
            <Button
              loading={busy?.id === proposal.id && busy.action === 'accept'}
              disabled={busy?.id === proposal.id}
              onClick={() => decide(proposal, 'accept')}
            >
              {t('profileProposals.accept')}
            </Button>
          </div>
        </Card>
      ))}

      {decided.length > 0 && (
        <details className="text-sm text-gray-500 dark:text-gray-400">
          <summary className="cursor-pointer">{t('profileProposals.history')}</summary>
          <ul className="mt-3 space-y-3">
            {decided.map((proposal) => (
              <li key={proposal.id}>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={proposal.status === 'accepted' ? 'success' : 'neutral'}>
                    {t(`profileProposals.status_${proposal.status}`)}
                  </Badge>
                  <span>
                    {t('profileProposals.decidedBy', {
                      proposer: proposal.proposed_by_name ?? '—',
                      decider: proposal.decided_by_name ?? '—',
                      when: formatDate(proposal.decided_at ?? proposal.created_at, i18n.language),
                    })}
                  </span>
                </div>
                <ChangesList
                  changes={proposal.changes}
                  before={proposal.status === 'accepted' ? (proposal.previous ?? undefined) : undefined}
                />
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}

/** Each field with its new value, struck-through old one first when `before` is given. */
function ChangesList({
  changes,
  before,
}: {
  changes: WorkspaceProfileProposal['changes']
  before?: Record<string, unknown>
}) {
  const { t } = useTranslation()
  const show = useDisplay()

  return (
    <dl className="mt-3 space-y-2 text-sm">
      {Object.entries(changes).map(([field, value]) => (
        <div key={field} className="grid gap-1 sm:grid-cols-[10rem_1fr]">
          <dt className="text-gray-500 dark:text-gray-400">{t(`workspaces.fields.${field}`)}</dt>
          <dd className="break-words text-gray-900 dark:text-gray-50">
            {before && (
              <del className="mr-2 text-gray-500 dark:text-gray-400">{show(field, before[field])}</del>
            )}
            <ins className="no-underline">{show(field, value)}</ins>
          </dd>
        </div>
      ))}
    </dl>
  )
}
